import { useEffect, useState } from "react";

import { Button } from "../../components/ui/button";
import { renderThumbnail } from "../../lib/pdf";

export type RailDrawing = {
  id: string;
  file_name: string;
  page_count: number;
  /** application/pdf | image/png | image/jpeg | image/webp */
  mime_type?: string | null;
};

export type PageRailProps = {
  drawings: RailDrawing[];
  activeDrawingId: string | null;
  /** 当前图纸的 PDF 地址（缩略图和主画布共用同一份已解析的 PDF）。 */
  url: string | null;
  pageIndex: number;
  pageCount: number;
  /** 每页已画了几个框，用来在导航里一眼看出哪页有内容。 */
  boxCounts: Record<number, number>;
  onSelectDrawing: (drawingId: string) => void;
  onSelectPage: (pageIndex: number) => void;
  onUpload: () => void;
};

/** 最左一列：图纸与页面导航（PDF 缩略图 + 每页框数）。 */
export function PageRail({
  drawings,
  activeDrawingId,
  url,
  pageIndex,
  pageCount,
  boxCounts,
  onSelectDrawing,
  onSelectPage,
  onUpload,
}: PageRailProps) {
  // 图片型图纸：一页就是一张图片，缩略图直接用原图，不走 pdf.js
  const isImage =
    drawings.find((drawing) => drawing.id === activeDrawingId)?.mime_type?.startsWith("image/") ?? false;

  return (
    <aside className="flex w-44 shrink-0 flex-col border-r border-[var(--color-border)] bg-white">
      <div className="flex items-center justify-between border-b border-[var(--color-border)] px-3 py-2">
        <span className="text-sm font-medium">图纸</span>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onUpload}>
          上传
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-2">
        {drawings.length === 0 ? (
          <p className="px-1 py-2 text-xs text-[var(--color-muted-foreground)]">
            还没有图纸，点上面的「上传」选一个 PDF。
          </p>
        ) : null}

        {drawings.length > 1 ? (
          <div className="mb-2 space-y-1">
            {drawings.map((drawing) => (
              <button
                type="button"
                key={drawing.id}
                onClick={() => onSelectDrawing(drawing.id)}
                className={`block w-full truncate rounded-md px-2 py-1.5 text-left text-xs ${
                  drawing.id === activeDrawingId
                    ? "bg-[var(--color-muted)] font-medium"
                    : "hover:bg-[var(--color-muted)]/60"
                }`}
                title={drawing.file_name}
              >
                {drawing.file_name}
              </button>
            ))}
          </div>
        ) : null}

        <div className="space-y-2">
          {Array.from({ length: pageCount }, (_, index) => {
            const active = index === pageIndex;
            const boxes = boxCounts[index] ?? 0;
            return (
              <button
                type="button"
                key={index}
                onClick={() => onSelectPage(index)}
                className={`block w-full rounded-md border p-1.5 text-left transition ${
                  active
                    ? "border-[var(--color-primary)] bg-[var(--color-muted)]"
                    : "border-[var(--color-border)] hover:bg-[var(--color-muted)]/60"
                }`}
              >
                <div className="flex items-center justify-between px-0.5 pb-1">
                  <span className="text-[12px] font-medium">第 {index + 1} 页</span>
                  {boxes > 0 ? (
                    <span className="rounded-full bg-[var(--color-primary)] px-1.5 text-[12px] text-white">
                      {boxes}
                    </span>
                  ) : (
                    <span className="text-[12px] text-[var(--color-muted-foreground)]">空</span>
                  )}
                </div>
                {/*
                  懒渲染：只画当前页附近 ±2 页的缩略图。
                  扫描版图纸（单页几 MB）如果一进来就串行渲 40 页，
                  会把主画布的图像解码挤到"未就绪"，中间大图就变成空白的。
                */}
                {url && Math.abs(index - pageIndex) <= 2 ? (
                  <PageThumb url={url} pageIndex={index} isImage={isImage} />
                ) : (
                  <div className="flex h-[88px] items-center justify-center rounded border border-dashed border-[var(--color-border)] text-[12px] text-[var(--color-muted-foreground)]">
                    {url ? "点开才渲染" : "待上传"}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </aside>
  );
}

/** 缩略图渲染队列：串行跑，别让几十页同时抢主线程把画布卡住。 */
let renderQueue: Promise<unknown> = Promise.resolve();
const enqueue = <T,>(task: () => Promise<T>): Promise<T> => {
  const run = renderQueue.then(task, task);
  renderQueue = run.catch(() => undefined);
  return run;
};

const thumbCache = new Map<string, string>();

function PageThumb({ url, pageIndex, isImage }: { url: string; pageIndex: number; isImage: boolean }) {
  const key = `${url}#${pageIndex}`;
  const [src, setSrc] = useState<string | null>(() => thumbCache.get(key) ?? null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // 图片型图纸本来就只有一页，媒体地址本身就是一张图，直接显示原图
    if (isImage) {
      setSrc(url);
      setFailed(false);
      return;
    }
    const cached = thumbCache.get(key);
    if (cached) {
      setSrc(cached);
      return;
    }
    let cancelled = false;
    setFailed(false);
    void enqueue(async () => {
      const dataUrl = await renderThumbnail(url, pageIndex, 132);
      thumbCache.set(key, dataUrl);
      return dataUrl;
    })
      .then((dataUrl) => {
        if (!cancelled) setSrc(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isImage, key, url, pageIndex]);

  return (
    <div className="flex h-[92px] items-center justify-center overflow-hidden rounded bg-white">
      {src && !failed ? (
        <img
          src={src}
          alt={`第 ${pageIndex + 1} 页缩略图`}
          className="h-full w-full object-contain"
          // 图片型图纸的"缩略图"就是原图，加载失败说明媒体地址本身有问题，照实提示
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="text-[12px] text-[var(--color-muted-foreground)]">
          {failed ? "缩略图失败" : "…"}
        </span>
      )}
    </div>
  );
}
